import { ENDPOINTS, getFullUrl } from "./config";
import {
  PaymentSummaryResponse,
  PaymentOrdersResponse,
  AppointmentPaymentResponse,
  ExpenseListingResponse,
  ExpenseCreatePayload,
  ExpenseTypeListingResponse,
  ExpenseTypeCreatePayload,
  BankDepositListingResponse,
  BankDepositCreatePayload,
  ClinicsFinancialListingResponse,
  MonthEndLogItem,
  MonthEndLogResponse,
  MonthEndLogListingResponse,
  MonthEndLogCreatePayload,
  MonthEndLogUpdatePayload,
} from "../../types/transactionHistory.types";

const TH = ENDPOINTS.TRANSACTION_HISTORY;

export class TransactionHistoryService {
  static getToken(): string | null {
    return localStorage.getItem("token");
  }

  private static headers(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.getToken()}`,
    };
  }

  // ─── Payment Summary ───────────────────────────────────────
  static async getPaymentSummary(params: {
    startDate: string;
    endDate: string;
    clinicId?: string;
  }): Promise<PaymentSummaryResponse> {
    try {
      const url = new URL(getFullUrl(TH.PAYMENT_SUMMARY));
      url.searchParams.append("startDate", params.startDate);
      url.searchParams.append("endDate", params.endDate);
      if (params.clinicId) url.searchParams.append("clinicId", params.clinicId);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch payment summary",
        data: null as any,
      };
    }
  }

  // ─── Clinics Financial Listing ─────────────────────────────
  static async getClinicsFinancialListing(params: {
    startDate: string;
    endDate: string;
    page?: number;
    pageSize?: number;
    searchText?: string;
  }): Promise<ClinicsFinancialListingResponse> {
    try {
      const url = new URL(getFullUrl(TH.CLINICS_FINANCIAL_LISTING));
      url.searchParams.append("startDate", params.startDate);
      url.searchParams.append("endDate", params.endDate);
      if (params.page) url.searchParams.append("page", params.page.toString());
      if (params.pageSize) url.searchParams.append("pageSize", params.pageSize.toString());
      if (params.searchText) url.searchParams.append("searchText", params.searchText);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch clinics listing",
        data: [],
        pagination: { totalItemsCount: 0, totalPages: 0 },
      };
    }
  }

  // ─── Payment Orders (Order Service) ────────────────────────
  static async getPaymentOrders(params: {
    startDate: string;
    endDate: string;
    clinicId?: string;
    type?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PaymentOrdersResponse> {
    try {
      const url = new URL(getFullUrl(TH.PAYMENT_ORDERS));
      url.searchParams.append("startDate", params.startDate);
      url.searchParams.append("endDate", params.endDate);
      if (params.clinicId) url.searchParams.append("clinicId", params.clinicId);
      if (params.type) url.searchParams.append("type", params.type);
      if (params.page) url.searchParams.append("page", params.page.toString());
      if (params.limit) url.searchParams.append("limit", params.limit.toString());
      if (params.search) url.searchParams.append("search", params.search);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch payment orders",
        data: { orders: [], pagination: { totalItemsCount: 0, totalPages: 0 } },
      };
    }
  }

  // ─── Appointment Payment Details ───────────────────────────
  static async getAppointmentPaymentDetails(params: {
    startDate: string;
    endDate: string;
    clinicIds?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AppointmentPaymentResponse> {
    try {
      const url = new URL(getFullUrl(TH.APPOINTMENT_PAYMENT_DETAILS));
      url.searchParams.append("startDate", params.startDate);
      url.searchParams.append("endDate", params.endDate);
      if (params.clinicIds) url.searchParams.append("clinicIds", params.clinicIds);
      if (params.page) url.searchParams.append("page", params.page.toString());
      if (params.pageSize) url.searchParams.append("pageSize", params.pageSize.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch appointment details",
        data: { payments: [], pagination: { totalItemsCount: 0, totalPages: 0 } },
      };
    }
  }

  // ─── Expense Listing ───────────────────────────────────────
  static async getExpenseListing(params: {
    startDate: string;
    endDate: string;
    clinicId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ExpenseListingResponse> {
    try {
      const url = new URL(getFullUrl(TH.EXPENSE_LISTING));
      url.searchParams.append("startDate", params.startDate);
      url.searchParams.append("endDate", params.endDate);
      if (params.clinicId) url.searchParams.append("clinicId", params.clinicId);
      if (params.page) url.searchParams.append("page", params.page.toString());
      if (params.pageSize) url.searchParams.append("pageSize", params.pageSize.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch expenses",
        data: [],
        totalAmount: 0,
        pagination: { totalItemsCount: 0, totalPages: 0 },
      };
    }
  }

  // ─── Create Expense ────────────────────────────────────────
  static async createExpense(payload: ExpenseCreatePayload): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const response = await fetch(getFullUrl(TH.EXPENSE_CREATE), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to create expense",
      };
    }
  }

  // ─── Expense Type Listing ──────────────────────────────────
  static async getExpenseTypes(params?: {
    page?: number;
    pageSize?: number;
    searchText?: string;
  }): Promise<ExpenseTypeListingResponse> {
    try {
      const url = new URL(getFullUrl(TH.EXPENSE_TYPE_LISTING));
      if (params?.page) url.searchParams.append("page", params.page.toString());
      if (params?.pageSize) url.searchParams.append("pageSize", params.pageSize.toString());
      if (params?.searchText) url.searchParams.append("searchText", params.searchText);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch expense types",
        data: [],
        pagination: { totalItemsCount: 0, totalPages: 0 },
      };
    }
  }

  // ─── Create Expense Type ───────────────────────────────────
  static async createExpenseType(payload: ExpenseTypeCreatePayload): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const response = await fetch(getFullUrl(TH.EXPENSE_TYPE_CREATE), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to create expense type",
      };
    }
  }

  // ─── Bank Deposit Listing ──────────────────────────────────
  static async getBankDepositListing(params: {
    startDate: string;
    endDate: string;
    clinicId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<BankDepositListingResponse> {
    try {
      const url = new URL(getFullUrl(TH.BANK_DEPOSIT_LISTING));
      url.searchParams.append("startDate", params.startDate);
      url.searchParams.append("endDate", params.endDate);
      if (params.clinicId) url.searchParams.append("clinicId", params.clinicId);
      if (params.page) url.searchParams.append("page", params.page.toString());
      if (params.pageSize) url.searchParams.append("pageSize", params.pageSize.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch bank deposits",
        data: [],
        totalAmount: 0,
        pagination: { totalItemsCount: 0, totalPages: 0 },
      };
    }
  }

  // ─── Create Bank Deposit ───────────────────────────────────
  static async createBankDeposit(payload: BankDepositCreatePayload): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const response = await fetch(getFullUrl(TH.BANK_DEPOSIT_CREATE), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to create bank deposit",
      };
    }
  }

  // ─── Get Month-End Log (by Month & Year) ───────────────────
  static async getMonthEndLog(params: {
    clinicId?: string;
    month: string;
    year: string | number;
  }): Promise<MonthEndLogResponse> {
    try {
      const url = new URL(getFullUrl(TH.MONTH_END_LOG_GET));
      if (params.clinicId) {
        url.searchParams.append("clinicId", params.clinicId);
        url.searchParams.append("clinic_id", params.clinicId);
      }
      url.searchParams.append("month", params.month);
      url.searchParams.append("year", params.year.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch month-end log",
        data: null,
      };
    }
  }

  // ─── Create Month-End Log ───────────────────────────────────
  static async createMonthEndLog(payload: MonthEndLogCreatePayload): Promise<MonthEndLogResponse> {
    try {
      const response = await fetch(getFullUrl(TH.MONTH_END_LOG_CREATE), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({
          clinicId: payload.clinicId || payload.clinic_id,
          clinic_id: payload.clinicId || payload.clinic_id,
          month: payload.month,
          year: payload.year,
          grossRevenue: payload.grossRevenue,
          gross_revenue: payload.grossRevenue,
          collectedOnline: payload.collectedOnline,
          collected_online: payload.collectedOnline,
          collectedCash: payload.collectedCash,
          collected_cash: payload.collectedCash,
          totalRevenue: payload.totalRevenue,
          total_revenue: payload.totalRevenue,
          totalRefund: payload.totalRefund,
          total_refund: payload.totalRefund,
          totalExpense: payload.totalExpense,
          total_expense: payload.totalExpense,
          totalDeposit: payload.totalDeposit,
          total_deposit: payload.totalDeposit,
          closingBalance: payload.closingBalance,
          closing_balance: payload.closingBalance,
        }),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to create month-end log",
        data: null,
      };
    }
  }

  // ─── Update Month-End Log ───────────────────────────────────
  static async updateMonthEndLog(
    id: number | string,
    payload: MonthEndLogUpdatePayload
  ): Promise<MonthEndLogResponse> {
    try {
      const response = await fetch(`${getFullUrl(TH.MONTH_END_LOG_UPDATE)}/${id}`, {
        method: "PUT",
        headers: this.headers(),
        body: JSON.stringify({
          id,
          ...payload,
          gross_revenue: payload.grossRevenue ?? payload.gross_revenue,
          collected_online: payload.collectedOnline ?? payload.collected_online,
          collected_cash: payload.collectedCash ?? payload.collected_cash,
          total_revenue: payload.totalRevenue ?? payload.total_revenue,
          total_refund: payload.totalRefund ?? payload.total_refund,
          total_expense: payload.totalExpense ?? payload.total_expense,
          total_deposit: payload.totalDeposit ?? payload.total_deposit,
          closing_balance: payload.closingBalance ?? payload.closing_balance,
        }),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update month-end log",
        data: null,
      };
    }
  }

  // ─── Month-End Logs Listing ────────────────────────────────
  static async getMonthEndLogsListing(params: {
    clinicId?: string;
    month?: string;
    year?: string | number;
    page?: number;
    pageSize?: number;
    searchText?: string;
  }): Promise<MonthEndLogListingResponse> {
    try {
      const url = new URL(getFullUrl(TH.MONTH_END_LOG_LISTING));
      if (params.clinicId) {
        url.searchParams.append("clinicId", params.clinicId);
        url.searchParams.append("clinic_id", params.clinicId);
      }
      if (params.month) url.searchParams.append("month", params.month);
      if (params.year) url.searchParams.append("year", params.year.toString());
      if (params.page) url.searchParams.append("page", params.page.toString());
      if (params.pageSize) url.searchParams.append("pageSize", params.pageSize.toString());
      if (params.searchText) url.searchParams.append("searchText", params.searchText);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch month-end logs listing",
        data: [],
      };
    }
  }
}
